/**
 * A template: the shape of the work, with no employee in it.
 *
 * PRD 6.3: a template defines task trees, branching and dependencies; an
 * instance is created from it when somebody starts or leaves. Nodes are
 * addressed by a `key` that is stable across versions of the same template,
 * which is what makes the opt-in migration of an in-flight instance (decision
 * R2) a diff of keys rather than a guess.
 *
 * Everything here is plain data -- it is stored as JSON and exported as JSON
 * (PRD 13.4), so nothing in it may be a function, a Date or a class.
 */

import type { DeadlineUnit } from "./settings.js";

export type LifecycleKind = "onboarding" | "offboarding";

/**
 * The offboarding trigger for somebody who is staying: only some of their
 * access is removed, and none of HR's separation cards apply (a client's
 * offboarding form, "employee staying"). It is an offboarding by kind, so it
 * is started, listed and run like one, but it is never a departure: the
 * Employees index does not count it as leaving (decisions.md).
 */
export const ACCESS_CHANGE = "access-change";

/** What people call one piece of work: an access change is never an "offboarding" to the person staying. */
export function workWord(
  kind: LifecycleKind,
  trigger: string,
): "onboarding" | "offboarding" | "access change" {
  if (kind === "onboarding") return "onboarding";
  return trigger === ACCESS_CHANGE ? "access change" : "offboarding";
}

/** What people call the day the work counts from: "start date", "last day", or the day access goes. */
export function anchorText(kind: LifecycleKind, trigger: string): string {
  if (kind === "onboarding") return "start date";
  return trigger === ACCESS_CHANGE ? "day access goes" : "last day";
}

/** PRD 6.3's branching dimensions. Everything a condition can test is on the profile, fixed at creation. */
export interface Profile {
  employeeType: string;
  employmentStatus: string;
  department: string;
  /** For offboarding: what started it (PRD 9.1). Empty for onboarding. */
  trigger: string;
  /** The resolved site flags, so "HIPAA sites only" is a condition like any other. */
  hipaa: boolean;
  /**
   * For an involuntary departure at a site whose state requires final pay on
   * the day: "at-meeting", so the card for having the check ready applies
   * (finalpay.ts). Empty or absent otherwise.
   */
  finalPay?: string;
  /**
   * The answers to the template's questions, given when the work is started:
   * one per question, never blank. Absent on everything started before
   * templates asked questions, and on a template that asks none.
   */
  answers?: Record<string, Answer>;
}

export type ProfileKey = Exclude<keyof Profile, "answers">;

/**
 * An answer to a template's question. "na" is "does not apply to this
 * person", which drops a card exactly as "no" would where the template says
 * so -- it is never "not known yet": every question is answered before the
 * work starts, so a card is never dropped because somebody skipped a box.
 */
export type Answer = "yes" | "no" | "na";
export const ANSWERS: readonly Answer[] = ["yes", "no", "na"];

/**
 * Something whoever starts the work is asked about this person, Yes / No /
 * Does not apply -- "Will they need access to CUI?" -- which a card's
 * conditions can test, so "if applicable" cards drop out per hire.
 */
export interface Question {
  key: string;
  text: string;
  /** What to think about when answering, or who knows. */
  help?: string;
}

/**
 * Include a node only when every clause holds. No clauses: always included.
 *
 * Three kinds, by what they test:
 * - `field`: the profile, fixed when the work starts (PRD 6.3).
 * - `question`: an answer to one of the template's questions, also fixed then.
 * - `recorded`: a value an earlier card records, known only once that card is
 *   finished (PRD 6.5's branching at run time: office or remote, chosen by
 *   the manager). The card waits on that card, and when its turn comes and
 *   the value does not match it is skipped by itself, with the value as the
 *   reason -- nobody is told about work they are not getting.
 *
 * `in`: the value is one of these. `notIn`: it is none of them.
 */
export type Condition =
  | { field: ProfileKey; op: "in" | "notIn"; values: (string | boolean)[] }
  | { question: string; op: "in" | "notIn"; values: Answer[] }
  | { recorded: FieldLink; op: "in" | "notIn"; values: string[] };

/**
 * When a task is due.
 *
 * - `anchor`: so many days from the start or departure date (PRD 8.2, 9.2).
 * - `trigger`: so many days, or minutes, from the moment the instance was
 *   created. The offboarding "Notice" phase is relative to the day notice was
 *   given, not to the departure date, and an involuntary termination's 30
 *   minute access SLA (PRD 9.3) is relative to the trigger and nothing else.
 *
 * `unit` absent means the site's default (business days unless the site runs
 * 24/7). Minutes are always wall-clock minutes.
 */
export type DeadlineSpec =
  | { from: "anchor"; days: number; unit?: DeadlineUnit }
  | { from: "trigger"; days: number; unit?: DeadlineUnit }
  | { from: "trigger"; minutes: number };

/** A day counted like a deadline's, in days only: when a card may open at the earliest. */
export interface DaySpec {
  from: "anchor" | "trigger";
  days: number;
  unit?: DeadlineUnit;
}

/** A value a task records, which a later task can read (PRD 6.5's cross-links). */
export interface FieldSpec {
  key: string;
  label: string;
  /** Marked at the template level (PRD 11.3) and treated differently everywhere after: see `cards.ts`. */
  pii?: boolean;
  /** A completion cannot be submitted without it. */
  required?: boolean;
  /** Fixed choices, when there are some. Free text otherwise. */
  options?: string[];
  /**
   * Any number of the choices rather than one: which safety trainings a
   * manager signs somebody up for. Stored as one value, the choices in the
   * order `options` lists them, joined by `CHOICE_SEPARATOR` (`choicesOf()`
   * reads it back). Needs `options`.
   */
  multiple?: boolean;
  /** On an onboarding: this records something issued, of this kind ("device", "badge", "keys"). See issued.ts. */
  issues?: string;
  /** On an offboarding: this records the return of that kind, checked against what was issued. */
  returns?: string;
}

/** "Show me field `field` of node `node`" -- a data dependency (PRD 7.2). */
export interface FieldLink {
  node: string;
  field: string;
}

export type NodeKind = "task" | "notify";

export interface TemplateNode {
  key: string;
  kind: NodeKind;
  title: string;
  description?: string;
  /** The department that owns it. One, deliberately: see docs/notes/decisions.md, "One owning department". */
  department: string;
  deadline: DeadlineSpec;
  /** Checklist lines. Completing the task needs all of them ticked. */
  subtasks?: string[];
  /** What completing this task records. */
  fields?: FieldSpec[];
  /** Values read from upstream tasks; the task cannot be completed until each one exists. */
  inputs?: FieldLink[];
  /**
   * A checklist line per choice made on an earlier card's multiple-choice
   * field, after the fixed `subtasks`: the manager picks eight trainings of
   * ten, and Safety's card lists those eight to tick. The card waits for that
   * card to be finished, as it does for a branch on what it records, and is
   * skipped by itself when nothing was chosen and it has no lines of its own.
   */
  checklistFrom?: FieldLink;
  conditions?: Condition[];
  /** Offboarding's "Time-Critical" column: shown as such, and escalated faster. */
  timeCritical?: boolean;
  /**
   * The card takes somebody's access away. A departure can ask for access to
   * go at a time of day on the last day (the instance's `cutoffTime`); a
   * deadline counted in days then falls at that time on its day rather than
   * at the end of it, and the card shows the time asked for beside the time
   * it was done (`accessTimes()`). Task cards only.
   */
  revokesAccess?: boolean;
  /**
   * A compliance task (orientation, I-9, a HIPAA briefing). On a site with the
   * HIPAA flag, completing one also needs the name of whoever reviewed it
   * (PRD 11.2).
   */
  compliance?: boolean;
  /**
   * The card is about final pay. When the site's state is known, its
   * description is replaced by what the law there says (`finalPayText()`).
   */
  finalPay?: boolean;
  /**
   * Other departments that must each sign off before the card can be
   * finished -- "Hardware retrieved: IT + Facilities". The owning department
   * does the work and presses Done; each department here adds its name first.
   * See docs/notes/decisions.md, "Sign-offs gate a card alongside its owner".
   */
  signoffs?: string[];
  /**
   * The card collects free-form notes, one per domain, each with an optional
   * link to the document it describes -- knowledge transfer (PRD 9.4), where
   * the manager and the leaver decide what the domains are, so a template
   * cannot list them.
   */
  notes?: boolean;
  /** Notify cards only: how loud the handoff is (PRD 9.3's URGENT). */
  urgency?: "normal" | "urgent";
  /**
   * Notify cards only: an address outside the organisation that is mailed
   * when the card fires, as well as the department -- a managed IT provider
   * that opens the tickets. The mail names the card and the person and says
   * nothing a card's title would not.
   */
  mailTo?: string;
  /**
   * The card does not open before this day, even with nothing left to wait
   * on: a 30-day check-in that would otherwise sit in HR's "Now" from the
   * day the onboarding starts. It opens at the start of that day, on the
   * site's clock.
   */
  notBefore?: DaySpec;
  /**
   * A gate: a check that has to be finished before what comes after it opens
   * -- identity or restricted-party screening before CUI access. Its arrows
   * out are hard, and it cannot be skipped, because a skipped card lets
   * whatever waits on it open (decisions.md, "Skipped satisfies a hard
   * dependency"). Task cards only.
   */
  gate?: boolean;
  /** A URL to the tool the work happens in (PRD 5.3, the Base tier's integration). */
  link?: string;
}

/**
 * `hard`: the later task cannot start until the earlier one is done.
 * `soft`: it can, with a warning (PRD 7.2).
 */
export type EdgeKind = "hard" | "soft";

export interface TemplateEdge {
  from: string;
  to: string;
  kind: EdgeKind;
}

/**
 * Which departures an offboarding template is for. Nobody picks it from a
 * list at the time (`offboardingTemplateFor()`): a termination runs on a
 * termination template, somebody staying on an access change, every other
 * reason on a standard one.
 */
export type OffboardingUse = "standard" | "termination" | "access-change";

export interface Template {
  id: string;
  name: string;
  version: number;
  kind: LifecycleKind;
  /** Offboarding only; absent is "standard". */
  use?: OffboardingUse;
  /** The sites it is offered at. Absent or empty: every site. */
  sites?: string[];
  /** The starter it was copied from, when it was. */
  basedOn?: string;
  /** Asked, Yes / No / Does not apply, when the work is started. */
  questions?: Question[];
  nodes: TemplateNode[];
  edges: TemplateEdge[];
}

/** Whether any card's conditions test this profile field: the start form and the preview ask only for those. */
export const testsField = (template: Pick<Template, "nodes">, field: ProfileKey): boolean =>
  template.nodes.some((node) =>
    node.conditions?.some((condition) => "field" in condition && condition.field === field),
  );

/**
 * What joins the choices of a multiple-choice field into the one value it is
 * stored as. A newline, because a choice is a single line (the template
 * validator refuses one with a line break in it), and the audit log then
 * shows the choices one to a line.
 */
export const CHOICE_SEPARATOR = "\n";

/** The choices in a stored value: none for a blank, one for a single choice or free text without a line break. */
export const choicesOf = (value: string | undefined): string[] =>
  value ? value.split(CHOICE_SEPARATOR).filter((choice) => choice !== "") : [];
